import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAdminMe,
  listCategories,
  createCategory,
  updateCategoryStatus,
  listServices,
  createService,
  updateServiceStatus,
  deleteService
} from '../api';
import AdminShell from './AdminShell';
import api, { ADMIN_API_URL } from '../api';
import '../styles/AdminDashboard.css';
import '../styles/GlassAdmin.css';

const ICON_OPTIONS = ['plumbing', 'electrical_services', 'cleaning_services', 'local_florist', 'spa', 'construction', 'category'];

const REACT_ICON_TO_MATERIAL = {
  FaWrench: 'plumbing',
  FaBolt: 'electrical_services',
  FaBroom: 'cleaning_services',
  FaSpa: 'spa',
  FaPaintRoller: 'construction',
  FaTruck: 'local_shipping',
  FaTruckMoving: 'local_shipping',
  FaHammer: 'construction',
  FaLeaf: 'local_florist',
  FaTree: 'local_florist',
  FaPaw: 'pets',
  FaCar: 'directions_car',
  FaHome: 'home',
  FaTools: 'construction'
};

function resolveIcon(rawIcon) {
  if (!rawIcon) return 'category';
  if (ICON_OPTIONS.includes(rawIcon)) return rawIcon;
  return REACT_ICON_TO_MATERIAL[rawIcon] || 'category';
}

export default function CategoryManagement() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    icon: 'category',
    iconType: 'existing',
    iconFile: null
  });
  const [iconPreview, setIconPreview] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
const fileInputRef = useRef(null);
  const [subsModalCategory, setSubsModalCategory] = useState(null);
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [newSubName, setNewSubName] = useState('');
  const [subError, setSubError] = useState('');

  const loadCategories = useCallback(() => {
    setLoading(true);
    listCategories().then(({ data }) => setCategories(data.categories)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    getAdminMe()
      .then(({ data }) => setAdmin(data.admin))
      .catch(() => {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        navigate('/login');
      });
    loadCategories();
  }, [navigate, loadCategories]);

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!newCategory.name.trim()) {
      setCreateError('Category name is required.');
      return;
    }
    setCreating(true);
    try {
      await createCategory(
        newCategory.name.trim(),
        newCategory.icon,
        newCategory.iconType === 'upload' ? newCategory.iconFile : null
      );
      setShowCreateModal(false);
      setNewCategory({ name: '', icon: 'category', iconType: 'existing', iconFile: null });
      setIconPreview('');
      loadCategories();
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create category.');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleCategory = async (cat) => {
    try {
      await updateCategoryStatus(cat.id, !cat.is_active);
      loadCategories();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update category.');
    }
  };

  const openSubsModal = (cat) => {
    setSubsModalCategory(cat);
    setSubError('');
    setNewSubName('');
    setSubsLoading(true);
    listServices(cat.id)
      .then(({ data }) => setSubs(data.services))
      .catch(() => {})
      .finally(() => setSubsLoading(false));
  };

  const reloadSubs = () => {
    if (!subsModalCategory) return;
    listServices(subsModalCategory.id).then(({ data }) => setSubs(data.services)).catch(() => {});
  };

  const handleAddSub = async (e) => {
    e.preventDefault();
    setSubError('');
    if (!newSubName.trim()) {
      setSubError('Sub-category name is required.');
      return;
    }
    try {
      await createService(subsModalCategory.id, newSubName.trim());
      setNewSubName('');
      reloadSubs();
      loadCategories();
    } catch (err) {
      setSubError(err.response?.data?.message || 'Failed to add sub-category.');
    }
  };

  const handleToggleSub = async (sub) => {
    try {
      await updateServiceStatus(sub.id, !sub.is_active);
      reloadSubs();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update sub-category.');
    }
  };

  const handleDeleteSub = async (sub) => {
    if (!window.confirm(`Delete "${sub.name}"?`)) return;
    try {
      await deleteService(sub.id);
      reloadSubs();
      loadCategories();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete sub-category.');
    }
  };

  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const activeCount = categories.filter((c) => c.is_active).length;
  const totalSubcats = categories.reduce((sum, c) => sum + (c.subcat_count || 0), 0);
  const totalVendors = categories.reduce((sum, c) => sum + (c.vendor_count || 0), 0);

  return (
    <AdminShell
      admin={admin}
      badges={{}}
      pageTitle="Category Management"
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search categories..."
    >
      <div className="glass-page">
        <div className="dash-welcome-row">
          <div>
            <h2 className="dash-welcome-title">Category Management</h2>
            <p className="dash-welcome-subtitle">Organize and manage service categories and sub-categories.</p>
          </div>
          <div className="dash-quick-actions">
            <button className="dash-quick-btn primary" onClick={() => setShowCreateModal(true)}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span>
              Add New Category
            </button>
          </div>
        </div>

        <div className="glass-kpi-grid">
          <article className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Total Categories</span>
              <span className="material-symbols-outlined glass-kpi-icon">category</span>
            </div>
            <div className="glass-kpi-value">{categories.length}</div>
            <div className="glass-kpi-sub">All service categories</div>
          </article>

          <article className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Active</span>
              <span className="material-symbols-outlined glass-kpi-icon">toggle_on</span>
            </div>
            <div className="glass-kpi-value">{activeCount}</div>
            <div className="glass-kpi-sub">Currently visible to vendors</div>
          </article>

          <article className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Sub-categories</span>
              <span className="material-symbols-outlined glass-kpi-icon">account_tree</span>
            </div>
            <div className="glass-kpi-value">{totalSubcats}</div>
            <div className="glass-kpi-sub">Across all categories</div>
          </article>

          <article className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Total Vendors</span>
              <span className="material-symbols-outlined glass-kpi-icon">storefront</span>
            </div>
            <div className="glass-kpi-value">{totalVendors}</div>
            <div className="glass-kpi-sub">Assigned to a category</div>
          </article>
        </div>

        {loading ? (
          <div className="dash-empty-state">Loading categories...</div>
        ) : filteredCategories.length === 0 ? (
          <div className="dash-empty-state">No categories match your search.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {filteredCategories.map((cat) => (
              <div key={cat.id} className={`glass-category-card ${!cat.is_active ? 'inactive' : ''}`}>
                <div className="dash-category-info">
                  <div className="dash-category-icon-wrap">
                    {cat.icon && (cat.icon.startsWith('/') || cat.icon.startsWith('http')) ? (
                      <img
  src={
    cat.icon.startsWith('http')
      ? cat.icon
      : `${ADMIN_API_URL.replace('/api/admin', '')}${cat.icon}`
  }
  alt=""
  className="category-card-custom-icon"
/>
                    ) : (
                      <span className="material-symbols-outlined" style={{ fontSize: 28 }}>
                        {resolveIcon(cat.icon)}
                      </span>
                    )}
                  </div>
                  <div>
                    <h3 className="dash-category-name">
                      {cat.name}
                      <span className={`dash-category-badge ${cat.is_active ? 'active' : 'inactive'}`}>
                        {cat.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </h3>
                  </div>
                </div>

                <div className="dash-category-metrics">
                  <div className="glass-category-metric-pill">
                    <p className="dash-metric-value">{cat.subcat_count}</p>
                    <p className="dash-metric-label">Sub-cats</p>
                  </div>
                  <div className="glass-category-metric-pill">
                    <p className="dash-metric-value">{cat.vendor_count}</p>
                    <p className="dash-metric-label">Vendors</p>
                  </div>
                </div>

                <div className="glass-category-actions">
                  <button className="dash-manage-subs-btn" onClick={() => openSubsModal(cat)}>
                    Manage SubCategories
                  </button>
                  <button
                    className={`glass-toggle-btn ${cat.is_active ? 'deactivate' : 'activate'}`}
                    onClick={() => handleToggleCategory(cat)}
                  >
                    {cat.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create category modal */}
        {showCreateModal && (
          <div className="dash-modal-backdrop" onClick={() => setShowCreateModal(false)}>
            <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="dash-modal-title">Add New Category</h3>
              {createError && <div className="admin-banner error">{createError}</div>}
              <form onSubmit={handleCreateCategory} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="dash-modal-field">
                  <label htmlFor="catName">Category Name</label>
                  <input
                    id="catName"
                    type="text"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                    placeholder="e.g. Pest Control"
                  />
                </div>
                <div className="dash-modal-field">
                  <label>Icon</label>

                  <div className="category-icon-choice-row">
                    <button
                      type="button"
                      className={`category-icon-choice ${newCategory.iconType === 'existing' ? 'active' : ''}`}
                      onClick={() =>
                        setNewCategory({
                          ...newCategory,
                          iconType: 'existing',
                          iconFile: null
                        })
                      }
                    >
                      <span className="material-symbols-outlined">category</span>
                      Use Existing Icon
                    </button>

                    <button
  type="button"
  className={`category-icon-choice ${
    newCategory.iconType === 'upload' ? 'active' : ''
  }`}
  onClick={() => {
    setNewCategory({
      ...newCategory,
      iconType: 'upload'
    });

    fileInputRef.current?.click();
  }}
>
  <span className="material-symbols-outlined">upload</span>
  Upload Custom Icon
</button>
                  </div>

                  {newCategory.iconType === 'existing' ? (
                    <select
                      id="catIcon"
                      className="admin-text-input"
                      value={newCategory.icon}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          icon: e.target.value
                        })
                      }
                    >
                      {ICON_OPTIONS.map((icon) => (
                        <option key={icon} value={icon}>
                          {icon}
                        </option>
                      ))}
                    </select>
                  ) : (
  <div>
    <input
      ref={fileInputRef}
      id="catIconFile"
      type="file"
      accept="image/png,image/jpeg,image/webp,image/svg+xml"
      style={{ display: 'none' }}
      onChange={(e) => {
        const file = e.target.files?.[0] || null;

        setNewCategory({
          ...newCategory,
          iconFile: file
        });

        if (file) {
          setIconPreview(URL.createObjectURL(file));
        } else {
          setIconPreview('');
        }
      }}
    />

    {iconPreview && (
      <img
        src={iconPreview}
        alt="Icon preview"
        className="category-icon-preview"
      />
    )}
  </div>
)}
                </div>
                <div className="dash-modal-actions">
                  <button type="button" className="dash-modal-btn secondary" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="dash-modal-btn primary" disabled={creating}>
                    {creating ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Manage Subs modal */}
        {subsModalCategory && (
          <div className="dash-modal-backdrop" onClick={() => setSubsModalCategory(null)}>
            <div className="dash-modal dash-modal-wide" onClick={(e) => e.stopPropagation()}>
              <div className="dash-modal-header-row">
                <div>
                  <h3 className="dash-modal-title">Manage Sub-categories</h3>
                  <p className="dash-modal-subtitle">{subsModalCategory.name}</p>
                </div>
                <button className="dash-icon-btn" onClick={() => setSubsModalCategory(null)}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              {subError && <div className="admin-banner error">{subError}</div>}

              <form onSubmit={handleAddSub} style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  className="admin-text-input"
                  style={{ flex: 1 }}
                  placeholder="New sub-category name..."
                  value={newSubName}
                  onChange={(e) => setNewSubName(e.target.value)}
                />
                <button type="submit" className="dash-modal-btn primary">Add</button>
              </form>

              <div className="dash-subs-list">
                {subsLoading ? (
                  <p className="dash-empty-state">Loading...</p>
                ) : subs.length === 0 ? (
                  <p className="dash-empty-state">No sub-categories yet.</p>
                ) : (
                  subs.map((sub) => (
                    <div key={sub.id} className="dash-sub-row">
                      <span className="dash-sub-name">{sub.name}</span>
                      <div className="dash-sub-actions">
                        <span className={`dash-status-pill ${sub.is_active ? 'active' : 'inactive'}`}>
                          {sub.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <button className="dash-link-btn edit" onClick={() => handleToggleSub(sub)}>
                          {sub.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button className="dash-link-btn deactivate" onClick={() => handleDeleteSub(sub)}>Delete</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}